import { useState, type ReactNode } from 'react';
import { AlertDialog as BaseAlertDialog } from '@base-ui/react/alert-dialog';
import { cn } from '../../lib/cn.ts';
import { Button } from '../button/button.tsx';
import { dialogContentVariants } from '../dialog/dialog.tsx';

export type ConfirmDialogVariant = 'default' | 'danger';

export interface ConfirmDialogProps {
  open: boolean;
  /**
   * Called for every close the user asks for — Cancel, Escape. It is not called
   * after `onConfirm`: what happens once the work is done is the caller's
   * decision, and a dialog that closed itself on a failed delete would take the
   * error message with it.
   */
  onOpenChange: (open: boolean) => void;
  /** The question, in the words of the thing being acted on. */
  title: ReactNode;
  /** One sentence: what this will do, or what cannot be undone. */
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /**
   * The work. While the promise is pending both buttons are disabled and the
   * confirm button shows the busy state, so a slow delete cannot be sent twice.
   */
  onConfirm: () => void | Promise<void>;
  variant?: ConfirmDialogVariant;
  /** Shown inside the dialog when the last attempt failed. */
  error?: string;
  className?: string;
}

/**
 * The question before something irreversible.
 *
 * Base UI's AlertDialog rather than the Dialog above it, for the two things
 * that make an alert an alert: `role="alertdialog"`, and a popup that cannot be
 * dismissed by clicking the page behind it. Everything visible — the popup
 * recipe, the buttons — is the same system the Dialog uses; only the semantics
 * differ.
 *
 * Component-only, no `useConfirm()` hook. The caller already owns the state the
 * hook would hold (which row is being deleted), so a hook would hand back an
 * `open` flag it also has to be told about, plus a portal to render somewhere.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  variant = 'default',
  error,
  className,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  /*
   * The words stay as opened while the popup animates closed: callers clear the
   * row it asks about the moment it closes, which would blank out its name.
   */
  const [shown, setShown] = useState({ title, description });
  if (open && (shown.title !== title || shown.description !== description)) {
    setShown({ title, description });
  }

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }

  return (
    <BaseAlertDialog.Root
      open={open}
      onOpenChange={(next) => {
        /* Work in flight: the question has been answered, wait for the answer. */
        if (!busy) {
          onOpenChange(next);
        }
      }}
    >
      <BaseAlertDialog.Portal>
        <BaseAlertDialog.Backdrop
          data-slot='dialog-backdrop'
          className='fixed inset-0 z-50 bg-scrim backdrop-blur-scrim'
        />
        <BaseAlertDialog.Popup
          data-slot='confirm-dialog'
          className={cn(dialogContentVariants({ size: 'md' }), 'p-5', className)}
        >
          <BaseAlertDialog.Title
            data-slot='dialog-title'
            className='text-md font-bold text-ink'
          >
            {shown.title}
          </BaseAlertDialog.Title>
          {shown.description !== undefined && shown.description !== null && (
            <BaseAlertDialog.Description
              data-slot='dialog-description'
              className='mt-1.5 text-sm text-ink-muted'
            >
              {shown.description}
            </BaseAlertDialog.Description>
          )}
          {error !== undefined && error !== '' && (
            <p
              data-slot='confirm-dialog-error'
              role='alert'
              className='mt-3.5 rounded-sm bg-danger-fill px-3 py-2 text-sm font-semibold text-danger'
            >
              {error}
            </p>
          )}
          <div className='mt-5 flex justify-end gap-2'>
            <BaseAlertDialog.Close
              render={<Button variant='quiet' size='sm' disabled={busy} />}
            >
              {cancelLabel}
            </BaseAlertDialog.Close>
            <Button
              data-slot='confirm-dialog-confirm'
              variant={variant === 'danger' ? 'danger' : 'primary'}
              size='sm'
              loading={busy}
              onClick={() => void handleConfirm()}
            >
              {confirmLabel}
            </Button>
          </div>
        </BaseAlertDialog.Popup>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  );
}
